import React from 'react';
import Modal from './Modal';
import '../../styles/thresholdUpdateModal.css';

export default function ThresholdUpdateModal({
    isOpen,
    onClose,
    updateResult
}) {
    if (!updateResult) return null;

    const {
        modalTitle = 'Threshold Update',
        success,
        changedThresholds = [],
        actionSummary,
        sensorMac,
        totalDevices,
        relevantDevices,
        successfulDevices = [],
        failedDevices = [],
        skippedDevices = [],
        relevantActuatorTypes = [],
        missingActuatorTypes
    } = updateResult;

    const classSafe = (value) => String(value).toLowerCase().replace(/[^a-z0-9_-]+/g, '-');

    const renderDeviceList = (devices, icon, statusClass) => {
        if (!devices || devices.length === 0) return null;

        return (
            <div className={`threshold-update-devices threshold-update-devices--${statusClass}`}>
                {devices.map((device, index) => (
                    <div key={index} className="threshold-update-device">
                        <i className={icon} aria-hidden="true"></i>
                        <span className="threshold-update-device-name">
                            {device.name || device.macAddress}
                        </span>
                        <span className="threshold-update-device-type">{device.actuatorType}</span>
                        {device.appliedDetail && (
                            <span className="threshold-update-device-detail">{device.appliedDetail}</span>
                        )}
                        {device.reason && (
                            <span className="threshold-update-device-reason">{device.reason}</span>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const hasDeviceDetails = (
        (missingActuatorTypes?.length > 0 && relevantDevices === 0)
        || successfulDevices.length > 0
        || failedDevices.length > 0
        || skippedDevices.length > 0
    );

    return (
        <Modal isVisible={isOpen} onClose={onClose}>
            <div className="threshold-update-modal">
                <header className="threshold-update-header">
                    <i
                        className={`threshold-update-icon ${success ? 'ri-check-circle-line threshold-update-icon--success' : 'ri-error-warning-line threshold-update-icon--error'}`}
                        aria-hidden="true"
                    ></i>
                    <div className="threshold-update-heading">
                        <h3 className="threshold-update-title">
                            {modalTitle} {success ? 'complete' : 'failed'}
                        </h3>
                        <p className="threshold-update-subtitle">
                            {actionSummary || (
                                <>
                                    {successfulDevices.length} of {relevantDevices} relevant device{relevantDevices === 1 ? '' : 's'} updated
                                    {failedDevices.length > 0 && ` · ${failedDevices.length} failed`}
                                    {skippedDevices.length > 0 && ` · ${skippedDevices.length} skipped`}
                                </>
                            )}
                        </p>
                        {actionSummary && (
                            <p className="threshold-update-outcome">
                                {successfulDevices.length} of {relevantDevices} updated
                                {failedDevices.length > 0 && ` · ${failedDevices.length} failed`}
                                {skippedDevices.length > 0 && ` · ${skippedDevices.length} skipped`}
                            </p>
                        )}
                    </div>
                </header>

                <div className="threshold-update-meta">
                    {changedThresholds.map((threshold) => (
                        <span key={threshold} className={`threshold-update-chip threshold-update-chip--threshold threshold-update-chip--${classSafe(threshold)}`}>
                            {threshold}
                        </span>
                    ))}
                    {sensorMac && (
                        <span className="threshold-update-chip threshold-update-chip--sensor">{sensorMac}</span>
                    )}
                    {relevantActuatorTypes.map((type) => (
                        <span key={type} className={`threshold-update-chip threshold-update-chip--type threshold-update-chip--${classSafe(type)}`}>
                            {type}
                        </span>
                    ))}
                </div>

                <div className="threshold-update-stats">
                    <span className="threshold-update-stat">
                        <strong>{totalDevices}</strong> connected
                    </span>
                    <span className="threshold-update-stat">
                        <strong>{relevantDevices}</strong> relevant
                    </span>
                    <span className="threshold-update-stat threshold-update-stat--success">
                        <strong>{successfulDevices.length}</strong> updated
                    </span>
                    {failedDevices.length > 0 && (
                        <span className="threshold-update-stat threshold-update-stat--error">
                            <strong>{failedDevices.length}</strong> failed
                        </span>
                    )}
                </div>

                {hasDeviceDetails && (
                    <div className="threshold-update-body">
                        {missingActuatorTypes?.length > 0 && relevantDevices === 0 && (
                            <section className="threshold-update-section threshold-update-section--warning">
                                <h4 className="threshold-update-section-title">
                                    <i className="ri-alert-line" aria-hidden="true"></i>
                                    Missing actuator types
                                </h4>
                                <p className="threshold-update-callout">
                                    No devices with the required types are connected to <strong>{sensorMac}</strong>.
                                    Connect devices for: {missingActuatorTypes.join(', ')}.
                                </p>
                                <div className="threshold-update-meta threshold-update-meta--inline">
                                    {missingActuatorTypes.map((type) => (
                                        <span key={type} className="threshold-update-chip threshold-update-chip--missing">
                                            {type}
                                        </span>
                                    ))}
                                </div>
                            </section>
                        )}

                        {successfulDevices.length > 0 && (
                            <section className="threshold-update-section">
                                <h4 className="threshold-update-section-title threshold-update-section-title--success">
                                    <i className="ri-check-line" aria-hidden="true"></i>
                                    Updated ({successfulDevices.length})
                                </h4>
                                {renderDeviceList(successfulDevices, 'ri-check-circle-fill', 'success')}
                            </section>
                        )}

                        {failedDevices.length > 0 && (
                            <section className="threshold-update-section">
                                <h4 className="threshold-update-section-title threshold-update-section-title--error">
                                    <i className="ri-error-warning-line" aria-hidden="true"></i>
                                    Failed ({failedDevices.length})
                                </h4>
                                {renderDeviceList(failedDevices, 'ri-close-circle-fill', 'error')}
                            </section>
                        )}

                        {skippedDevices.length > 0 && (
                            <section className="threshold-update-section">
                                <h4 className="threshold-update-section-title threshold-update-section-title--skipped">
                                    <i className="ri-skip-forward-line" aria-hidden="true"></i>
                                    Skipped ({skippedDevices.length})
                                </h4>
                                {renderDeviceList(skippedDevices, 'ri-skip-forward-fill', 'skipped')}
                            </section>
                        )}
                    </div>
                )}

                <footer className="threshold-update-footer">
                    <button
                        type="button"
                        className="threshold-update-close-btn"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </footer>
            </div>
        </Modal>
    );
}
